import { useState, useEffect, useRef, useCallback } from "react";
import slotsConfig from "../SlotsConfig";

// ── Grid constants (must match VendorMap.jsx) ──────────────────────────────────
const CELL = 22;
// Must match VendorMap.jsx's PAD exactly — both files compute the same
// viewBox independently, and panToSlot's math below only lines up with
// what's actually rendered if these stay in sync.
const PAD  = 140;

// ── Parse grid value e.g. "3 / span 2" → { start, span } ─────────────────────
function parseGrid(val) {
  if (!val) return { start: 1, span: 1 };
  const str = String(val).trim();
  const m = str.match(/(\d+)\s*\/?\s*span\s*(\d+)/i);
  if (m) return { start: parseInt(m[1]), span: parseInt(m[2]) };
  const n = str.match(/(\d+)/);
  return { start: n ? parseInt(n[1]) : 1, span: 1 };
}

// ── Find the pixel centre of a named slot ─────────────────────────────────────
function getSlotCentre(slotId) {
  const slot = slotsConfig.find(s => s.id === slotId);

  if (!slot) {
    // Fallback: first real vendor slot
    const fallback = slotsConfig.find(
      s => s.type !== "pathway" && s.type !== "blocker" && s.id !== "P1"
    );
    if (!fallback) return null;
    const c = parseGrid(fallback.gridColumn);
    const r = parseGrid(fallback.gridRow);
    return {
      x: (c.start - 1) * CELL + (c.span * CELL) / 2,
      y: (r.start - 1) * CELL + (r.span * CELL) / 2,
    };
  }

  const c = parseGrid(slot.gridColumn);
  const r = parseGrid(slot.gridRow);
  // Pixel centre in SVG-space (before viewBox offset)
  const x = (c.start - 1) * CELL + (c.span * CELL) / 2;
  const y = (r.start - 1) * CELL + (r.span * CELL) / 2;
  return { x, y };
}

// ── Compute the viewBox origin (same as VendorMap.jsx) ────────────────────────
const { VB_X, VB_Y, VB_W, VB_H } = (() => {
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  slotsConfig.forEach(s => {
    const c = parseGrid(s.gridColumn);
    const r = parseGrid(s.gridRow);
    const x = (c.start - 1) * CELL;
    const y = (r.start - 1) * CELL;
    const w = c.span * CELL;
    const h = r.span * CELL;
    minX = Math.min(minX, x);     minY = Math.min(minY, y);
    maxX = Math.max(maxX, x + w); maxY = Math.max(maxY, y + h);
  });
  return {
    VB_X: minX - PAD, VB_Y: minY - PAD,
    VB_W: maxX - minX + PAD * 2,
    VB_H: maxY - minY + PAD * 2,
  };
})();

// ── Compute pan offset to centre a slot in the viewport ───────────────────────
function panToSlot(slotId, mapWrapEl, zoom = 1.4) {
  const centre = getSlotCentre(slotId);
  if (!centre || !mapWrapEl) return null;

  const rect = mapWrapEl.getBoundingClientRect();
  const vpW  = rect.width;
  const vpH  = rect.height;

  // The SVG renders with preserveAspectRatio="xMidYMid meet"
  // so we need to account for letterboxing (unused space on one axis)
  const scaleX     = vpW / VB_W;
  const scaleY     = vpH / VB_H;
  const baseScale  = Math.min(scaleX, scaleY); // "meet" uses the smaller

  // Offset from letterboxing — the SVG is centred inside the element
  const letterboxX = (vpW - VB_W * baseScale) / 2;
  const letterboxY = (vpH - VB_H * baseScale) / 2;

  const totalScale = baseScale * zoom;

  // Where the slot centre sits in screen px at zoom=1, including letterbox
  const slotScreenX = letterboxX + (centre.x - VB_X) * baseScale;
  const slotScreenY = letterboxY + (centre.y - VB_Y) * baseScale;

  // Pan at the new zoom so that point lands in the viewport centre
  return {
    x: vpW / 2 - slotScreenX * zoom,
    y: vpH / 2 - slotScreenY * zoom,
  };
}

// ── Reactive isMobile ──────────────────────────────────────────────────────────
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${breakpoint - 1}px)`);
    const handler = e => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    setIsMobile(mq.matches);
    return () => mq.removeEventListener("change", handler);
  }, [breakpoint]);

  return isMobile;
}

// ── Hook ───────────────────────────────────────────────────────────────────────
// The map is now always full-bleed / "fullscreen" — there is no toggled
// state anymore, so this hook just owns pan/zoom + touch gestures
// unconditionally, for as long as the component is mounted.
export function useMapInteraction(mapWrapRef) {
  const isMobile     = useIsMobile();
  const [mobilePan,  setMobilePan]  = useState({ x: 0, y: 0 });
  const [mobileZoom, setMobileZoom] = useState(1);

  const isPanning  = useRef(false);
  const panStart   = useRef({ mx: 0, my: 0, px: 0, py: 0 });
  const lastPinch  = useRef(null);

  // Pan & zoom to Entry once on mount — mobile only. panToSlot's math
  // assumes the transformed element (.vmap-transform) is sized in
  // VB_W x VB_H units, which VendorMap.jsx only does when isMobile is
  // true; on desktop that element is 100% of its parent (real viewport
  // pixels), so running this there would compute a transform in the
  // wrong coordinate space and move the map off-screen. Desktop instead
  // always shows the full map at rest (mobilePan stays {0,0}, mobileZoom
  // stays 1, and VendorMap.jsx doesn't apply the transform style at all
  // when !isMobile).
  useEffect(() => {
    if (!isMobile) return;

    const t = setTimeout(() => {
      const ENTRY_ZOOM = 1.6; // zoom in a bit so Entry is clearly visible
      const offset = panToSlot("Entry", mapWrapRef.current, ENTRY_ZOOM);
      if (offset) {
        setMobileZoom(ENTRY_ZOOM);
        setMobilePan(offset);
      }
    }, 80);

    return () => clearTimeout(t);
  }, [isMobile, mapWrapRef]);

  // ── Touch handlers ────────────────────────────────────────────────────────
  const onTouchStart = useCallback(e => {
    if (e.touches.length === 1) {
      isPanning.current = true;
      lastPinch.current = null; // cancel any lingering pinch state
      panStart.current = {
        mx: e.touches[0].clientX,
        my: e.touches[0].clientY,
        px: mobilePan.x,
        py: mobilePan.y,
      };
    } else if (e.touches.length >= 2) {
      // Second finger down — switch from pan to pinch
      isPanning.current = false;
      // Snapshot the initial finger distance fresh each time
      lastPinch.current = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  }, [mobilePan]);

  const onTouchMove = useCallback(e => {
    e.preventDefault();
    if (e.touches.length === 1 && isPanning.current) {
      setMobilePan({
        x: panStart.current.px + e.touches[0].clientX - panStart.current.mx,
        y: panStart.current.py + e.touches[0].clientY - panStart.current.my,
      });
    } else if (e.touches.length === 2 && lastPinch.current !== null) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      // Only apply if fingers actually moved meaningfully (avoids micro-jitter spikes)
      if (Math.abs(dist - lastPinch.current) > 0.5) {
        const ratio = dist / lastPinch.current;
        // Update ref BEFORE setState so the next frame reads the fresh value
        lastPinch.current = dist;
        setMobileZoom(z => Math.max(0.5, Math.min(3, z * ratio)));
      }
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    isPanning.current = false;
    lastPinch.current = null;
  }, []);

  // Attach touchmove with { passive: false } so we can preventDefault.
  // No longer gated on isFullscreen — the map is always interactive.
  useEffect(() => {
    const el = mapWrapRef.current;
    if (!el) return;
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, [onTouchMove, mapWrapRef]);

  // ── Desktop drag-to-pan + wheel-to-zoom ─────────────────────────────────
  // Mirrors the mobile touch pan/pinch above, but works in plain screen
  // pixels (no VB_W/VB_H coordinate conversion needed) since the desktop
  // .vmap-transform box is always sized to 100% of its container, not a
  // fixed pixel box like the mobile one.
  const [desktopPan,  setDesktopPan]  = useState({ x: 0, y: 0 });
  const [desktopZoom, setDesktopZoom] = useState(1);
  const [isDragging,  setIsDragging]  = useState(false);
  const dragStart = useRef({ mx: 0, my: 0, px: 0, py: 0 });

  // Reset if the viewport crosses the mobile breakpoint while mounted.
  useEffect(() => {
    if (isMobile) {
      setDesktopPan({ x: 0, y: 0 });
      setDesktopZoom(1);
    }
  }, [isMobile]);

  const onMouseDown = useCallback(e => {
    if (e.button !== 0) return; // left-click only
    dragStart.current = {
      mx: e.clientX,
      my: e.clientY,
      px: desktopPan.x,
      py: desktopPan.y,
    };
    setIsDragging(true);
  }, [desktopPan]);

  // Track mousemove/mouseup on the window, not just the map element, so a
  // drag that leaves the map's bounds while the button is held keeps
  // working — same reasoning as any map library's drag handling.
  useEffect(() => {
    if (!isDragging) return;

    const onMove = e => {
      setDesktopPan({
        x: dragStart.current.px + (e.clientX - dragStart.current.mx),
        y: dragStart.current.py + (e.clientY - dragStart.current.my),
      });
    };
    const onUp = () => setIsDragging(false);

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDragging]);

  // Wheel-to-zoom, keeping the point under the cursor fixed on screen.
  // Attached manually (not via React's onWheel prop) so preventDefault
  // reliably stops the page itself from scrolling while zooming the map.
  useEffect(() => {
    const el = mapWrapRef.current;
    if (!el || isMobile) return;

    const onWheel = e => {
      e.preventDefault();
      const rect   = el.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      setDesktopZoom(prevZoom => {
        const factor  = Math.exp(-e.deltaY * 0.0015);
        const newZoom = Math.max(1, Math.min(4, prevZoom * factor));

        setDesktopPan(prevPan => {
          // Convert the cursor's screen position to "content" space at
          // the old zoom, then re-project it at the new zoom — this is
          // what keeps the point under the cursor visually fixed.
          const contentX = (mouseX - prevPan.x) / prevZoom;
          const contentY = (mouseY - prevPan.y) / prevZoom;
          return {
            x: mouseX - newZoom * contentX,
            y: mouseY - newZoom * contentY,
          };
        });

        return newZoom;
      });
    };

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [mapWrapRef, isMobile]);

  // Zoom by a fixed factor toward the center of the viewport — same
  // "keep a point fixed on screen" math as the wheel handler above, just
  // anchored to the map's center instead of the cursor. Powers the
  // desktop +/- zoom control buttons.
  const zoomBy = useCallback(factor => {
    const el = mapWrapRef.current;
    if (!el) return;
    const rect    = el.getBoundingClientRect();
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;

    setDesktopZoom(prevZoom => {
      const newZoom = Math.max(1, Math.min(4, prevZoom * factor));

      setDesktopPan(prevPan => {
        const contentX = (centerX - prevPan.x) / prevZoom;
        const contentY = (centerY - prevPan.y) / prevZoom;
        return {
          x: centerX - newZoom * contentX,
          y: centerY - newZoom * contentY,
        };
      });

      return newZoom;
    });
  }, [mapWrapRef]);

  const zoomIn  = useCallback(() => zoomBy(1.3),      [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / 1.3),  [zoomBy]);

  return {
    isMobile,
    mobilePan,
    mobileZoom,
    onTouchStart,
    onTouchEnd,
    desktopPan,
    desktopZoom,
    isDragging,
    onMouseDown,
    zoomIn,
    zoomOut,
  };
}